// Device manager: a single access point to live data and trainer control.
import { trainerCurve, clampWheel } from '@gruvetto/core';
import { Trainer } from './trainer.js';
import { HeartRate } from './heartRate.js';
import { PowerMeter } from './powerMeter.js';
import { SpeedCadence } from './speedCadence.js';
import { ZwiftController, DEFAULT_RIDE_MAP, buttonLabel } from './zwiftController.js';
import { fuse, record, type Latest, type PowerOrigin, type SourceId } from './fusion.js';
import type { Reading } from './gatt.js';
import { bluetooth, needBluetooth, type BtDevice, type StatusText } from './ble.js';
import { SCAN_OPTIONS, SCAN_SERVICES, classify, requestFailure, knownDevices, rememberDevice, forgetKind, type DeviceKind } from './discovery.js';
import { state, update } from '../data/store.js';
import { t, onCultureChange } from '../i18n/index.js';
import { input } from './controls.js';
import { isGameAction } from './input.js';

/** Actions assignable to controller buttons (labels: t.devices.actions). */
export const ACTIONS = { steerLeft: 'steerLeft', steerRight: 'steerRight', fire: 'fire', gearUp: 'gearUp', gearDown: 'gearDown', look: 'look', camera: 'camera', pause: 'pause' } as const;

export type DeviceId = DeviceKind;
type Status = 'on' | 'off' | 'wait';

/** Merged live data (power W, cadence rpm, speed km/h, heart rate bpm). */
export interface Live { power: number; cadence: number; cadenceKnown: boolean; speed: number; hr: number; origin: PowerOrigin }

const HR_FRESH_MS = 5000;

class DeviceManager extends EventTarget {
  hr: HeartRate; trainer: Trainer; power: PowerMeter; csc: SpeedCadence; ride: any;
  lastButton: string | null = null; mapWait: string | null = null; raw: string[] = [];
  status = {} as Record<DeviceId, [Status, string]>;
  /** Message of the last failed search (raw text or translated function), empty otherwise. */
  private scanText: StatusText = '';
  private statusText = {} as Record<DeviceId, StatusText>;
  private src: Record<SourceId | 'hr', Latest> = { meter: {}, trainer: {}, csc: {}, hr: {} };

  constructor() {
    super();
    for (const k of ['trainer', 'power', 'csc', 'hr'] as const) this.setStatus(k, 'off', () => t.devices.notConnected);
    this.setStatus('ride', 'off', () => t.devices.notConnectedPlural);
    // Translatable status texts are functions: re-evaluated when the language changes.
    onCultureChange((_, langChanged) => { if (!langChanged) return; for (const k in this.status) this.setStatus(k as DeviceId, this.status[k][0], this.statusText[k]); });
    const wheel = this.wheelMm;
    this.trainer = new Trainer(d => this.data(d, 'trainer'), (s, x) => this.setStatus('trainer', s, x));
    this.trainer.wheelMm = wheel;
    this.power = new PowerMeter(d => this.data(d, 'meter'), (s, x) => this.setStatus('power', s, x), wheel);
    this.csc = new SpeedCadence(d => this.data(d, 'csc'), (s, x) => this.setStatus('csc', s, x), wheel);
    this.hr = new HeartRate(d => this.data(d, 'hr'), (s, x) => this.setStatus('hr', s, x));
    this.ride = new ZwiftController((k, down) => this.button(k, down), (txt, cls) => this.setStatus('ride', cls === 'ok' ? 'on' : cls === 'bad' ? 'off' : 'wait', txt), (tag, bytes) => this.rawLog(tag, bytes));
  }
  get rideMap() { return state.settings.rideMap || { ...DEFAULT_RIDE_MAP }; }

  // ---- classic trainer settings ----
  get curve() { return trainerCurve(state.settings.trainerCurve); }
  get wheelMm() { return clampWheel(state.settings.wheelMm); }
  setCurve(id: string | null) { update(s => { s.settings.trainerCurve = trainerCurve(id) ? id : null; }); this.dispatchEvent(new CustomEvent('status')); }
  setWheel(mm: number) {
    const w = clampWheel(mm);
    update(s => { s.settings.wheelMm = w; });
    this.trainer.wheelMm = w; this.power.wheelMm = w; this.csc.wheelMm = w;
  }

  // ---- measurements ----
  private data(d: Reading, src: SourceId | 'hr') {
    record(this.src[src], d, performance.now());
    this.dispatchEvent(new CustomEvent('data', { detail: this.live }));
  }
  /** Measurements merged at the current instant: a source silent for more than 3 s no longer counts. */
  get live(): Live {
    const now = performance.now(), f = fuse(this.src, this.curve, now);
    const fresh = (L: Latest) => (L.hr && now - L.hr.at <= HR_FRESH_MS ? L.hr.v : null);
    // a dedicated heart-rate sensor takes precedence over the one relayed by the trainer
    const hr = fresh(this.src.hr) ?? (this.hr.connected ? null : fresh(this.src.trainer)) ?? 0;
    return { ...f, hr };
  }
  /** A real power measurement (or an estimate from wheel speed) is coming in: otherwise, keyboard simulation. */
  get powerLive() { return this.live.origin != null; }
  get hrLive() { return this.live.hr; }
  /** A device expected to provide power is connected (used to tell "simulation" from "no data"). */
  get powerDeviceConnected() { return this.trainer.connected || this.power.connected || (this.csc.connected && !!this.curve); }

  /** Aggregated state for the menu's "trainer" chip: the best power source. */
  get powerStatus(): [Status, string] {
    const order: DeviceId[] = ['power', 'trainer', ...(this.curve ? ['csc' as const] : [])];
    for (const s of ['on', 'wait'] as const) for (const k of order) if (this.status[k][0] === s) return [s, this.sourceName(k)];
    return ['off', ''];
  }
  private sourceName(k: DeviceId) { return k === 'trainer' ? this.trainer.name : k === 'power' ? this.power.name : this.csc.name; }

  /** `text`: raw text (device name, error) or a function returning translated text. */
  setStatus(dev: DeviceId, s: Status, text: StatusText) {
    this.statusText[dev] = text;
    this.status[dev] = [s, typeof text === 'function' ? text() : text];
    this.dispatchEvent(new CustomEvent('status', { detail: { dev, s, text } }));
  }
  rawLog(tag: string, bytes: Uint8Array) {
    this.raw.unshift(`${tag}: ${[...bytes].map(b => b.toString(16).padStart(2, '0')).join(' ')}`);
    this.raw.length = Math.min(this.raw.length, 40);
    this.dispatchEvent(new CustomEvent('raw'));
  }
  button(key: string, down: boolean) {
    if (down) this.lastButton = key;
    if (this.mapWait && down) {
      const map = this.rideMap;
      for (const k in map) if (map[k] === this.mapWait) delete map[k];
      map[key] = this.mapWait; this.mapWait = null;
      update(s => { s.settings.rideMap = map; });
      this.dispatchEvent(new CustomEvent('mapped'));
      return;
    }
    const a = this.rideMap[key];
    this.dispatchEvent(new CustomEvent('button', { detail: { key, down, action: a, label: buttonLabel(key) } }));
    if (isGameAction(a)) input.press('controller', key, a, down);
  }

  get scanNote() { return typeof this.scanText === 'function' ? this.scanText() : this.scanText; }
  private setScanNote(text: StatusText) { this.scanText = text; this.dispatchEvent(new CustomEvent('status')); }
  /** Whether this browser can bring paired devices back without a chooser (not Chrome's default today). */
  get autoConnectSupported() { return !!bluetooth()?.getDevices; }

  /** A closed chooser changes nothing: a device already connected stays so, and its status with it. */
  private requestFailed(e: { name?: string; message?: string }, report: (text: StatusText) => void) {
    const failure = requestFailure(e);
    if (failure === 'unavailable') report(() => t.devices.bluetoothOff(e?.message ?? ''));
    else if (failure === 'other') report(e?.message || String(e));
  }

  async connect(dev: DeviceId) {
    try {
      const picked: BtDevice = await this[dev].connect();
      rememberDevice(picked.id, dev);
    } catch (e) { this.requestFailed(e, text => this.setStatus(dev, 'off', text)); }
  }
  disconnect(dev: DeviceId) { forgetKind(dev); this[dev].disconnect(); }

  /** One chooser for every kind of device: the pick goes to the slot its services call for. Call from a click. */
  async scan() {
    this.setScanNote('');
    let dev: BtDevice;
    try { dev = await needBluetooth().requestDevice(SCAN_OPTIONS); } catch (e) { this.requestFailed(e, text => this.setScanNote(text)); return; }
    try {
      const kind = await this.kindOf(dev);
      if (!kind) { dev.gatt?.disconnect(); this.setScanNote(() => t.devices.unsupported(dev.name ?? '')); return; }
      await this[kind].adopt(dev);
      rememberDevice(dev.id, kind);
    } catch (e) { console.warn('scan', dev.name, e); dev.gatt?.disconnect(); this.setScanNote(() => t.devices.connectFailed(dev.name ?? '')); }
  }
  private async kindOf(dev: BtDevice) {
    const server = await dev.gatt?.connect();
    const found: (number | string)[] = [];
    for (const s of SCAN_SERVICES) { try { await server?.getPrimaryService(s); found.push(s); } catch { /* service absent */ } }
    return classify(found);
  }

  /** At launch, brings back the paired devices the browser still authorises: now if in range, else when they advertise. */
  async autoConnect() {
    const b = bluetooth();
    if (!b?.getDevices) return;
    const known = knownDevices();
    let granted: BtDevice[] = [];
    try { granted = await b.getDevices(); } catch (e) { console.warn('getDevices', e); }
    await Promise.all(granted.filter(d => known[d.id]).map(d => this.bringBack(d, known[d.id])));
  }
  private async bringBack(dev: BtDevice, kind: DeviceId) {
    if (this[kind].connected) return;
    try { await this[kind].adopt(dev); return; } catch { this.setStatus(kind, 'off', () => (kind === 'ride' ? t.devices.notConnectedPlural : t.devices.notConnected)); }
    const stop = new AbortController();
    // the listener goes with the watch: aborted on the first advertisement, or when the browser refuses to watch
    dev.addEventListener('advertisementreceived', () => {
      stop.abort();
      if (!this[kind].connected) this[kind].adopt(dev).catch((e: unknown) => console.warn('auto-connect', dev.name, e));
    }, { once: true, signal: stop.signal });
    await dev.watchAdvertisements?.({ signal: stop.signal }).catch(e => { stop.abort(); console.warn('watchAdvertisements', dev.name, e); });
  }
}

export const devices = new DeviceManager();
